import { compareDesc } from 'flowhawk-core';
import type { ChatroomInfo, ChatroomInfoMap, ChatroomSort } from './types';

const getLastMessageSentTime = (chatInfo: ChatroomInfo | null | undefined) => {
  if (chatInfo && chatInfo.lastMessage) {
    return chatInfo.lastMessage.sentTime;
  }

  return null;
};

const getAlpha = (chatInfo: ChatroomInfo | null | undefined) => {
  if (chatInfo && chatInfo.name) {
    return chatInfo.name;
  }

  return null;
};

export const getBadgeCount = (
  chatInfo: { totalMessages: number; clearedCount: number; readCount: number; sentCount: number } | null | undefined,
) => {
  if (
    chatInfo &&
    typeof chatInfo.totalMessages === 'number' &&
    typeof chatInfo.sentCount === 'number' &&
    typeof chatInfo.readCount === 'number' &&
    typeof chatInfo.clearedCount === 'number'
  ) {
    return chatInfo.totalMessages - chatInfo.sentCount - chatInfo.readCount - chatInfo.clearedCount;
  }

  return null;
};

const getLastActiveTime = (chatInfo: ChatroomInfo | null | undefined) => {
  if (chatInfo && chatInfo.lastActiveTime) {
    return chatInfo.lastActiveTime;
  }

  return null;
};

const getCreateDate = (chatInfo: ChatroomInfo | null | undefined) => {
  if (chatInfo && chatInfo.createDate) {
    return chatInfo.createDate;
  }

  return null;
};

const sortChatrooms = (
  chats: ChatroomInfoMap | null | undefined,
  chatAId: number,
  chatBId: number,
  sortType: ChatroomSort,
): number => {
  // Sort by pins if either room has a pin
  if (chats && (chats[chatAId].pinNumber || chats[chatBId].pinNumber)) {
    return (chats[chatBId].pinNumber || 0) - (chats[chatAId].pinNumber || 0);
  }

  switch (sortType) {
    case 'ALPHABETICAL': {
      const alphaA = (chats ? getAlpha(chats[chatAId]) : null) || '';
      const alphaB = (chats ? getAlpha(chats[chatBId]) : null) || '';

      return alphaA.localeCompare(alphaB);
    }

    case 'BADGE_COUNT': {
      const chatA = chats?.[chatAId];
      const chatB = chats?.[chatBId];
      const badgeA = getBadgeCount(chatA) || 0;
      const badgeB = getBadgeCount(chatB) || 0;

      // When sorting by badge, we want any room with and unread to appear first
      // and of those, sort by last message time. As a fallback, if we happen to
      // have two rooms with the exact same last message time, sort by room id
      if ((badgeA > 0 && badgeB > 0) || badgeA === badgeB) {
        const timeA = getLastMessageSentTime(chatA) || '1970-01-01';
        const timeB = getLastMessageSentTime(chatB) || '1970-01-01';

        if (timeA === timeB) {
          return (chatA?.id || 0) - (chatB?.id || 0);
        }

        return compareDesc(timeA, timeB);
      }

      return badgeB - badgeA;
    }

    case 'MY_ACTIVE': {
      const myActiveA = (chats ? getLastActiveTime(chats[chatAId]) : null) || '1970-01-01';
      const myActiveB = (chats ? getLastActiveTime(chats[chatBId]) : null) || '1970-01-01';

      return compareDesc(myActiveA, myActiveB);
    }

    case 'CREATE_DATE': {
      const createDateA = (chats ? getCreateDate(chats[chatAId]) : null) || '1970-01-01';
      const createDateB = (chats ? getCreateDate(chats[chatBId]) : null) || '1970-01-01';

      return compareDesc(createDateA, createDateB);
    }

    default: {
      // DEFAULT
      const sentTimeA = (chats ? getLastMessageSentTime(chats[chatAId]) : null) || '1970-01-01';
      const sentTimeB = (chats ? getLastMessageSentTime(chats[chatBId]) : null) || '1970-01-01';

      return compareDesc(sentTimeA, sentTimeB);
    }
  }
};

export default sortChatrooms;
